import { subscribe, getState } from '../state.js';
import { navigate } from '../router.js';
import { getTranslatedFields, translate } from '../lib/i18n.js';
import { createTreeRenderer } from '../components/treeRenderer.js';
import { createGraphSearch } from '../components/graphSearch.js';
import { showCrosslinkInSidebar } from '../components/graphSidebar.js';
import { api } from '../api.js';
import type { ApiGraphNode, ApiRelationTypeTranslation } from '../../shared/types.js';

interface RelationType {
  id: number;
  label: string;
  translations?: ApiRelationTypeTranslation[];
}

interface TreeEdge {
  parent: string;
  child: string;
  relation_type_id?: number | null;
  [key: string]: unknown;
}

interface TreeData {
  nodes: ApiGraphNode[];
  edges: TreeEdge[];
  relationTypes?: RelationType[];
}

function getTranslatedRelationType(rt: RelationType): string {
  const { lang } = getState();
  if (!lang || !rt.translations || rt.translations.length === 0) return rt.label;
  const tr = rt.translations.find((r: ApiRelationTypeTranslation) => r.lang.toLowerCase() === lang.toLowerCase());
  return (tr && tr.name) ? tr.name : rt.label;
}

export async function renderTreeView(container: HTMLElement): Promise<void | (() => void)> {
  container.innerHTML = `<div class="view"><p class="view__subtitle" style="padding:4rem 2rem;text-align:center;">${translate('common.loading', 'loading...')}</p></div>`;

  let renderer: ReturnType<typeof createTreeRenderer> | null = null;
  let search: ReturnType<typeof createGraphSearch> | null = null;
  let langUnsub: (() => void) | null = null;
  let keyHandler: ((e: KeyboardEvent) => void) | null = null;
  let selectedTypeId: number | null = null; // null = show all

  try {
    const treeData = await api.getTreeGraph() as TreeData;
    const relationTypes: RelationType[] = treeData.relationTypes || [];

    // Auto-select first type if there are multiple
    if (relationTypes.length > 1) {
      selectedTypeId = relationTypes[0].id;
    }

    function filterDataByType(typeId: number | null): { nodes: ApiGraphNode[]; edges: TreeEdge[] } {
      if (typeId === null || relationTypes.length <= 1) return treeData;

      const filteredEdges = treeData.edges.filter((e: TreeEdge) => e.relation_type_id === typeId);
      const usedSlugs = new Set<string>();
      for (const e of filteredEdges) {
        usedSlugs.add(e.parent);
        usedSlugs.add(e.child);
      }
      const filteredNodes = treeData.nodes.filter((n: ApiGraphNode) => usedSlugs.has(n.slug));
      return { nodes: filteredNodes, edges: filteredEdges };
    }

    function render(): void {
      if (search) { search.destroy(); search = null; }
      if (renderer) { renderer.destroy(); renderer = null; }
      if (keyHandler) { document.removeEventListener('keydown', keyHandler); keyHandler = null; }
      container.innerHTML = '';

      const view = document.createElement('div');
      view.className = 'view view--tree';

      // Header (filter bar only, no title)
      const header = document.createElement('div');
      header.className = 'tree-header';

      if (treeData.nodes.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'not-found';
        empty.style.minHeight = '40vh';
        empty.innerHTML = `<p class="not-found__message">${translate('tree.empty', 'No hierarchical relationships defined yet.')}</p>`;
        view.appendChild(empty);
        container.appendChild(view);
        return;
      }

      // Relation type filter (only show if multiple types)
      if (relationTypes.length > 1) {
        const filterBar = document.createElement('div');
        filterBar.className = 'tree-filter';

        const label = document.createElement('label');
        label.className = 'tree-filter__label';
        label.textContent = translate('tree.relationship', 'Relationship');
        label.setAttribute('for', 'tree-type-select');

        const select = document.createElement('select');
        select.id = 'tree-type-select';
        select.className = 'tree-filter__select';

        for (const type of relationTypes) {
          const option = document.createElement('option');
          option.value = String(type.id);
          option.textContent = getTranslatedRelationType(type);
          if (type.id === selectedTypeId) {
            option.selected = true;
          }
          select.appendChild(option);
        }

        select.addEventListener('change', () => {
          selectedTypeId = Number(select.value);
          render();
        });

        filterBar.appendChild(label);
        filterBar.appendChild(select);
        header.appendChild(filterBar);
      }

      view.appendChild(header);

      // Filter data for selected type
      const filtered = filterDataByType(selectedTypeId);

      if (filtered.nodes.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'not-found';
        empty.style.minHeight = '40vh';
        empty.innerHTML = `<p class="not-found__message">${translate('tree.empty_type', 'No relationships of this type.')}</p>`;
        view.appendChild(empty);
        container.appendChild(view);
        return;
      }

      // Translate node titles
      const translatedData = {
        nodes: filtered.nodes.map((n: ApiGraphNode) => ({
          ...n,
          title: getTranslatedFields(n).title,
        })),
        edges: filtered.edges,
      };

      // Flex wrapper: graph on left, sidebar on right (same as graph view)
      const graphWrapper = document.createElement('div');
      graphWrapper.className = 'tree-graph-wrapper';

      const treeContainer = document.createElement('div');
      treeContainer.className = 'tree-graph-container';
      graphWrapper.appendChild(treeContainer);

      // Sidebar (hidden by default)
      const sidebar = document.createElement('div');
      sidebar.className = 'graph-sidebar';
      sidebar.style.display = 'none';
      sidebar.innerHTML = `
        <button class="graph-sidebar__close">&times;</button>
        <div class="graph-sidebar__content"></div>
      `;
      graphWrapper.appendChild(sidebar);

      // Legend
      const legend = document.createElement('div');
      legend.className = 'tree-legend';
      legend.innerHTML = `
        <span class="tree-legend__item"><span class="tree-legend__key">${translate('legend.click', 'click')}</span> ${translate('tree.legend_concept', 'node to view concept')}</span>
        <span class="tree-legend__item"><span class="tree-legend__key">${translate('legend.drag', 'drag')}</span> ${translate('tree.legend_pan', 'to pan')}</span>
        <span class="tree-legend__item"><span class="tree-legend__key">${translate('legend.scroll', 'scroll')}</span> ${translate('legend.to_zoom', 'to zoom')}</span>
      `;
      treeContainer.appendChild(legend);

      view.appendChild(graphWrapper);
      container.appendChild(view);

      // Sidebar logic
      const closeSidebar = (): void => {
        sidebar.style.display = 'none';
        if (renderer) renderer.clearEdgeHighlight();
      };
      (sidebar.querySelector('.graph-sidebar__close') as HTMLButtonElement).addEventListener('click', closeSidebar);

      keyHandler = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') closeSidebar();
      };
      document.addEventListener('keydown', keyHandler);

      const showEdgeSidebar = ({ edgeData }: { edgeData: TreeEdge | null; parentSlug: string; childSlug: string }): void => {
        if (!edgeData) return;
        const rerender = (): void => { closeSidebar(); render(); };
        showCrosslinkInSidebar(sidebar, edgeData as unknown as Parameters<typeof showCrosslinkInSidebar>[1], translatedData.nodes, {
          onReverse: rerender,
          onDelete: rerender,
        });
      };

      renderer = createTreeRenderer(treeContainer, translatedData, {
        onNodeClick: (slug: string) => navigate(`/concept/${slug}`),
        onEdgeClick: showEdgeSidebar,
      });

      search = createGraphSearch(treeContainer, translatedData.nodes, renderer);
    }

    render();

    langUnsub = subscribe('lang', render);

    return () => {
      if (search) search.destroy();
      if (renderer) renderer.destroy();
      if (langUnsub) langUnsub();
      if (keyHandler) document.removeEventListener('keydown', keyHandler);
    };
  } catch (err) {
    container.innerHTML = `<div class="view"><p class="form-error" style="padding:4rem 2rem;text-align:center;">${translate('tree.error', 'Failed to load tree')}: ${(err as Error).message}</p></div>`;
  }
}
